/**
 * Every row of a paginated list endpoint.
 *
 * The API pages every list (LimitOffsetPagination, 50 rows unless `limit` is given),
 * and a view that reads one page and shows it as "the list" silently hides the rest —
 * a clinician with 59 documents had their 2024 agreements, reference letters and CV
 * fall off the staff drawer's Documents tab, which read as "never uploaded" and led to
 * a duplicate upload. A list that is meant to be complete walks the pages until the
 * API reports no next page; a list that cannot be complete pages visibly instead.
 */

export type Page<T> = {
  count: number;
  next?: string | null;
  results: T[];
};

/** Rows per request: few round trips, well under any server-side ceiling. */
export const FETCH_ALL_PAGE_SIZE = 200;

/**
 * Walk `fetchPage` from offset 0 until the API reports no next page and return one
 * response in the same shape, with every row. `count` is the API's own total so a
 * caller can still assert rows and count agree.
 */
export async function fetchAllPages<T>(
  fetchPage: (page: { limit: number; offset: number }) => Promise<Page<T>>,
  pageSize = FETCH_ALL_PAGE_SIZE
): Promise<Page<T>> {
  const results: T[] = [];
  let offset = 0;
  let count = 0;
  for (;;) {
    const page = await fetchPage({ limit: pageSize, offset });
    const rows = page?.results ?? [];
    results.push(...rows);
    count = page?.count ?? results.length;
    offset += rows.length;
    // The API's `next` is the signal: a short page is NOT the end while `next` is set
    // (a server may cap `limit` below what we asked). No `next` (a test double, an
    // array-shaped response) or an empty page ends the walk.
    if (!page?.next || rows.length === 0) {
      break;
    }
  }
  return { count: Math.max(count, results.length), next: null, results };
}
